import { defineStore } from 'pinia'
import { ref } from 'vue'

import env from '~/libs/env'
import { useBorrowerStore } from '~/store/borrower'
import { useContactGuestStore } from '~/store/contact-guest'
import { get } from 'es-toolkit/compat'
import { CREDIT_BLOCK_STATUS } from '~/libs/credit-constants'

const KNOWN_BLOCK_STATUSES = Object.values(CREDIT_BLOCK_STATUS)

// The guest credit-check API sends the block status as an error `key` on a 422. Any other
// status code, or no error carrying a known status, resolves to null (generic credit error).
function guestCreditBlockStatus(err) {
  const errors = get(err, 'response.data.errors')

  if (get(err, 'response.status') !== 422 || !Array.isArray(errors)) {
    return null
  }

  return errors.find(error => KNOWN_BLOCK_STATUSES.includes(error?.key))?.key ?? null
}

export const useCreditStore = defineStore('credit', () => {
  const { $axios } = useNuxtApp()
  const borrowerStore = useBorrowerStore()
  const contactGuestStore = useContactGuestStore()

/*
  ███████ ████████  █████  ████████ ███████
  ██         ██    ██   ██    ██    ██
  ███████    ██    ███████    ██    █████
       ██    ██    ██   ██    ██    ██
  ███████    ██    ██   ██    ██    ███████
  STATE
*/
  const creditScore = ref(null)
  const connecting = ref(false)
  const loading = ref(false)
  const error = ref(null)
  const creditBlockStatus = ref(null)
  // Set when the self-pull rejected the borrower's name: 'lastName' or 'firstName', with the
  // borrower-facing copy in invalidNameMessage.
  const invalidNameField = ref(null)
  const invalidNameMessage = ref(null)
  // Set when the self-pull rejected the borrower's home address: 'street', 'city', 'state' or
  // 'zip', with the borrower-facing copy in invalidAddressMessage.
  const invalidAddressField = ref(null)
  const invalidAddressMessage = ref(null)
  const failedAttempts = ref(0)

/*
   ██████  ███████ ████████ ████████ ███████ ██████  ███████
  ██       ██         ██       ██    ██      ██   ██ ██
  ██   ███ █████      ██       ██    █████   ██████  ███████
  ██    ██ ██         ██       ██    ██      ██   ██      ██
   ██████  ███████    ██       ██    ███████ ██   ██ ███████
  GETTERS
*/

/*
   █████   ██████ ████████ ██  ██████  ███    ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ████   ██ ██
  ███████ ██         ██    ██ ██    ██ ██ ██  ██ ███████
  ██   ██ ██         ██    ██ ██    ██ ██  ██ ██      ██
  ██   ██  ██████    ██    ██  ██████  ██   ████ ███████
  ACTIONS
  ! - - Actions calling other actions in the same store must use `this.actionName(...)`
  ! - - If we do not use `this.actionName` it will not be properly mockable in tests.
  ! - - Computeds and refs will work fine, and should be called directly though.
*/
  async function getCreditScore(fail = false) {
    loading.value = true
    connecting.value = true
    error.value = null
    creditBlockStatus.value = null
    invalidNameField.value = null
    invalidNameMessage.value = null
    invalidAddressField.value = null
    invalidAddressMessage.value = null
    const borrowerId = borrowerStore.borrowerId

    if (fail || failedAttempts.value >= 4) {
      connecting.value = false
      loading.value = false
      error.value = 'too many attempts'
      return
    }

    let res = await $axios
      .post(`${env('apiUrl')}/bpapi-credit-report/borrower/${borrowerId}`)
      .catch((err) => {
        connecting.value = false
        loading.value = false
        error.value = err
        creditBlockStatus.value = get(err, 'response.data.meta.creditBlockStatus', null)
        invalidNameField.value = get(err, 'response.data.meta.invalidNameField', null)
        invalidNameMessage.value = invalidNameField.value ? get(err, 'response.data.meta.message', null) : null
        invalidAddressField.value = get(err, 'response.data.meta.invalidAddressField', null)
        invalidAddressMessage.value = invalidAddressField.value ? get(err, 'response.data.meta.message', null) : null
        failedAttempts.value = failedAttempts.value + 1
        return
      })
    if (!res) {
      return null
    } else {
      connecting.value = false
      loading.value = false
      const status = get(res, 'data.status', 'Error')
      if (status !== 'success') {
        failedAttempts.value = failedAttempts.value + 1
        error.value = get(res, 'data.data', 'error')
        return
      }
      creditScore.value = parseInt(res.data.data)
      return res.data.data
    }
  }

  async function guestCreditScore({ fail, answers }) {
    loading.value = true
    connecting.value = true
    error.value = null
    creditBlockStatus.value = null

    if (fail || failedAttempts.value >= 4) {
      connecting.value = false
      loading.value = false
      error.value = 'too many attempts'
      return
    }

    const res = await $axios
      .post(`${env('apiUrl')}/contact/guest/additional-owners/credit-check`, {
          ownerSsn: answers['contact.ssn'],
          ownerBirthDate: answers.ownerBirthDate,
        }, {
          headers: {
            Authorization: `lendio-jwt ${contactGuestStore.token}`,
          },
        })
      .then(response => {
        return response?.data
      })
      .catch(err => {
        connecting.value = false
        loading.value = false
        error.value = err
        creditBlockStatus.value = guestCreditBlockStatus(err)
        failedAttempts.value = failedAttempts.value + 1
        return { errored: true, error: err }
      })

    connecting.value = false
    loading.value = false

    if (!res) {
      error.value = 'No response from server'
      failedAttempts.value = failedAttempts.value + 1
      return null
    }

    return res
  }

  function reset() {
    creditScore.value = null
    connecting.value = false
    loading.value = false
    error.value = null
    creditBlockStatus.value = null
    invalidNameField.value = null
    invalidNameMessage.value = null
    invalidAddressField.value = null
    invalidAddressMessage.value = null
    failedAttempts.value = 0
  }

  return {
    // STATE
    creditScore,
    loading,
    connecting,
    error,
    creditBlockStatus,
    invalidNameField,
    invalidNameMessage,
    invalidAddressField,
    invalidAddressMessage,
    failedAttempts,

    // GETTERS

    // ACTIONS
    getCreditScore,
    guestCreditScore,
    reset,
  }
})
